import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DurationFormatPipe } from '../../../../Pipes/duration-format.pipe';
import { Broadcast } from '../../../../models/broadcast';
import { ClarityModule, ClrCheckboxModule } from '@clr/angular';
import { finalize, interval, Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { LogEventsService } from '../../services/log-events.service';
import { LogEvent } from '../../../../models/log-event';
import { TranscriptionEditorComponent } from '../../../transcription/components/transcription-editor/transcription-editor.component';
import { FormsModule } from '@angular/forms';
import { EmergencyStopButtonComponent } from '../emergency-stop-button/emergency-stop-button.component';
import { WebrtcAudioService } from '../../services/webrtc-audio.service';
import { VoxtralCorrectionContextEditorComponent } from '../voxtral-correction-context-editor/voxtral-correction-context-editor.component';
import { VoxtralExportReviewComponent } from '../voxtral-export-review/voxtral-export-review.component';
import { BroadcastAuditLogComponent } from './broadcast-audit-log/broadcast-audit-log.component';
import { VoxtralLiveDataComponent } from './voxtral-live-data/voxtral-live-data.component';
import { parseSpeakerMarker, speakerFromRecord, VoxtralSpeaker } from './voxtral-speaker';
import { voxtralExportFileName } from './voxtral-export-download';
import { downloadBlob } from '../../../../shared/utils/file-download';
import { defaultFabUrlForChannel } from '../voxtral-fab-defaults';
import { AuthorizationService } from '../../../../../security/authorization.service';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { DisableUnlessPermissionDirective } from '../../../../../security/disable-unless-permission.directive';

/**
 * How long an operator flip of "FAB nach DDS" outranks the broadcast row.
 * Covers the POST + DDB write + GraphQL publisher round-trip; after it, a
 * change made elsewhere (schedule dialog, second operator) wins again.
 */
export const FAB_DDS_GATE_ECHO_GRACE_MS = 30000;

export interface FabDdsGateSync {
  /** Row value: `undefined` = the payload did not carry the field at all. */
  rowValue: boolean | undefined;
  /** What the checkbox currently shows. */
  current: boolean;
  /** Operator intent still waiting for the row to echo it, else null. */
  desired: boolean | null;
  desiredSince: number;
  now: number;
  /** A POST for this flag is in flight. */
  saving: boolean;
  /** The panel switched to a different broadcast — take the row as-is. */
  force: boolean;
}

/**
 * 2026-09-02 — decide the "FAB nach DDS Start und Stop aktivieren" checkbox
 * state for a freshly pushed broadcast row.
 *
 * RCA (PRD, LIVE/2026/0902 1055/ORFS, Fußball Frauen-Europacup): the flag was
 * persisted as false, but neither GraphQL document selected
 * voxtralMini4bRealtimeIsFabDdsStartStopActive, so every row the client held
 * carried `undefined` and the old `!== false` seed read that as true. Closing
 * and reopening the Sendung re-ticked the box and brought back "FAB gesperrt
 * bis 11:46 (DDS-Fenster)" — leaving the operator unable to tell whether FAB
 * was receiving anything.
 *
 * Rules, in order:
 *   1. `undefined` row value is UNKNOWN, never "true" — keep what we show.
 *   2. `force` (different broadcast in the panel) — take the row, drop intent.
 *   3. A POST in flight, or an unechoed operator flip inside the grace
 *      window, outranks the row: the row lags the click by a round-trip and
 *      a stale payload would visibly flip the box back under the operator.
 *   4. Otherwise the row wins, and a matching (or expired) intent clears.
 */
export function syncFabDdsGate(i: FabDdsGateSync): { value: boolean; desired: boolean | null } {
  if (i.rowValue === undefined) return { value: i.current, desired: i.desired };
  if (i.force) return { value: i.rowValue, desired: null };
  if (i.saving) return { value: i.current, desired: i.desired };
  if (i.desired !== null) {
    const echoed = i.rowValue === i.desired;
    const expired = i.now - i.desiredSince > FAB_DDS_GATE_ECHO_GRACE_MS;
    if (!echoed && !expired) return { value: i.current, desired: i.desired };
  }
  return { value: i.rowValue, desired: null };
}

@Component({
  selector: 'app-details',
  imports: [CommonModule, DurationFormatPipe, ClarityModule, TranscriptionEditorComponent, FormsModule, ClrCheckboxModule, EmergencyStopButtonComponent, VoxtralCorrectionContextEditorComponent, VoxtralExportReviewComponent, BroadcastAuditLogComponent, VoxtralLiveDataComponent, HasPermissionDirective, DisableUnlessPermissionDirective],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss',
  standalone: true,
})
export class DetailsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() broadcast: any;

  // RBAC — Template-Gates für Controls, die kein Fieldset erfassen kann
  // (Buttons mit bestehendem [disabled], cds-icon-Pencils, NOT-AUS-Div).
  private readonly authz = inject(AuthorizationService);
  get canControlVoxtral(): boolean { return this.authz.hasPermission('control-voxtral'); }
  get canManageBroadcasts(): boolean { return this.authz.hasPermission('manage-broadcasts'); }

  private timerSub!: Subscription;
  private logEventsSub!: Subscription;

  isActive = false;
  isProcessing = false;
  transcriptionLines: string[] = [];

  showLogsModal = false;
  isLoadingLogs = false;
  logs: string[] = [];
  savingKiut = false;
  savingLiveStreamSource = false;

  editingStartTime = false;
  editingDuration = false;
  startTimeInput = '';   // "HH:mm:ss"
  durationInput = '';    // "HH:mm:ss"
  editingRedsysSendungsname = false;
  redsysNameInput = '';

  // Accordion panel state (clr-accordion, multi-panel). Allgemein starts
  // open; Voxtral-Steuerung (+ Live-Daten on wide screens) auto-opens on the
  // inactive→engaged edge (see ngOnChanges) so a manual close is respected.
  open = {
    allgemein: true,
    voxtral: false,
    voxtralLive: false,
    export: false,
    pcms: false,
    transkription: false,
    editor: false,
    audit: false,
  };
  private lastVoxtralEngaged = false;

  // Viewport flag (<768px): drives the emergency-stop size, hides the iframe
  // embed toggle and keeps Live-Daten closed on auto-open.
  isNarrow = false;
  private narrowMql: MediaQueryList | null = null;
  private readonly narrowMqlListener = (e: MediaQueryListEvent) => { this.isNarrow = e.matches; };

  // Voxtral Mini 4B Realtime
  voxtralStarting = false;
  voxtralStopping = false;
  voxtralCurrentText = '';
  voxtralCurrentHtml: SafeHtml = '';
  voxtralTranscriptLines: string[] = [];
  voxtralTranscriptHtml: SafeHtml[] = [];
  // 2026-09-28 — live speaker diarization (portal display only, never FAB).
  // voxtralTranscriptSpeakers runs in lockstep with Lines/Html (same
  // unshift / pop / reset); null where a line carried no speaker.
  voxtralCurrentSpeaker: VoxtralSpeaker | null = null;
  voxtralTranscriptSpeakers: (VoxtralSpeaker | null)[] = [];
  voxtralDiarizationEnabled = false;
  voxtralDiarizationSaving = false;
  voxtralDiarizationPending = false;
  // Echo of voxtral's diarization_state after a live toggle ("active",
  // "unavailable" on an EC2 whose AMI has no diarizer, ...); null = unknown.
  voxtralDiarizationState: string | null = null;
  voxtralExportDownloading = false;
  // Auto-subtitle bracketing banners (start/end/interval). Rendered as a
  // faded grey badge above the transcript, not included in any export.
  voxtralSystemMessages: { kind: string; text: string; ts: number }[] = [];
  voxtralError = '';
  voxtralFabEnabled = false;
  // Replaced at ngOnInit with the channel-aware default from
  // defaultFabUrlForChannel(broadcast.channel). The literal stays as a
  // safe fallback for any code path that consults it before init.
  voxtralFabUrl = 'http://10.128.16.38/livetranscription/send';
  voxtralFabSendType = 'cursor';
  voxtralCorrectionContextExpanded = false;
  voxtralCorrectionContext = '';
  voxtralCorrectionContextSaving = false;
  voxtralCorrectionContextError = '';
  voxtralCorrectionContextSavedAt: Date | null = null;
  voxtralCorrectionEnabled = false;
  // Mode enum (2026-08 — LLM modes removed with the llama-corrector stack).
  // `voxtralCorrectionEnabled` is kept as a derived boolean (true iff mode
  // != 'off') for back-compat with template bits that still read the flag.
  voxtralCorrectionMode: 'off' | 'fuzzy' | 'phonetic' = 'phonetic';
  // Live-toggle UI state. While a POST is in flight we show a spinner; while
  // a toggle has been persisted but the new mode has not yet taken effect
  // (waiting for the next FINAL inside the session runner) we show a "pending"
  // badge that clears when the WS payload echoes a matching correction_mode.
  voxtralCorrectionToggling = false;
  voxtralCorrectionPending = false;
  voxtralCorrectionPendingTarget: string | null = null;
  // FAB live-toggle pending flag — same UX pattern as the correction toggle.
  // (Spinner state itself is voxtralFabEnabledSaving, declared with the other
  //  *-Saving flags below.)
  voxtralFabPending = false;
  voxtralFabPendingTarget: boolean | null = null;
  // 2026-07-02 — per-broadcast DDS-interval FAB gate toggle. Default true
  // (checkbox checked). Persisted as broadcast.voxtralMini4bRealtimeIsFabDdsStartStopActive
  // and live-pushed to the running voxtral session via POST
  // /broadcast/voxtral-fab-dds-start-stop-active.
  voxtralIsFabDdsStartStopActive = true;
  voxtralIsFabDdsStartStopActiveSaving = false;
  voxtralIsFabDdsStartStopActivePending = false;
  voxtralIsFabDdsStartStopActivePendingTarget: boolean | null = null;
  // 2026-09-02 — operator intent still waiting for its echo on the broadcast
  // row. Non-null from the POST until the first pushed row that agrees; while
  // non-null a lagging row must not re-seed the checkbox. See
  // syncIsFabDdsStartStopActiveFromRow().
  private fabDdsGateDesired: boolean | null = null;
  private fabDdsGateDesiredSince = 0;
  // Which broadcast the Voxtral panel state was seeded from — the panel is
  // re-used across rows, and the @Input reference is replaced on every live
  // push (programplan clones the row), so identity is not a usable signal.
  private seededBroadcastKey: string | null = null;
  // 2026-08-04 — operator recovery from a wrong "Vortranskribiert" flag, see
  // onResetPretranscribed().
  resetPretranscribedSaving = false;
  resetPretranscribedError = '';
  // 2026-06-08 — mid-broadcast FAB-URL live-update plumbing (mirrors the
  // enable toggle's state). Saving = HTTP in flight; Pending = persisted +
  // live-update returned `live:true`, brief "Wird übernommen…" badge.
  voxtralFabUrlSaving = false;
  voxtralFabUrlPending = false;
  voxtralFabUrlPendingTarget: string | null = null;
  private voxtralFabUrlDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  // F3: rolling list of recent corrections for the admin panel.
  // Each entry: { original, corrected, ts (ms), scope ('partial'|'final') }.
  voxtralRecentCorrections: Array<{ original: string; corrected: string; ts: number; scope: string }> = [];
  // Phase 4 observability: gate-rejected correction candidates, polled from
  // the C# backend proxy `GET /api/broadcast/voxtral-corrections`. Separate
  // list so operators can distinguish "applied" vs "blocked".
  voxtralRecentRejections: Array<{
    original: string;
    candidate: string;
    reason: string;
    ngram: number;
    ts: number;
    stage: string;
    duration_ms: number;
    original_segment: string;
    corrected_segment: string;
    language: string;
    model_id: string;
    scope?: string;        // 2026-05-19 — partial / final tag from voxtral state ring
  }> = [];
  private rejectionsPollSub?: Subscription;

  // Raw snapshot of the Voxtral session's /api/sessions/{id}/corrections
  // endpoint, refreshed every 10s alongside the rejection poll. Powers the
  // collapsible "Korrektur-API" panel — useful for debugging cases where the
  // WS stream looks empty (e.g. session started before llama-corrector was up)
  // and the operator wants to confirm what the server actually has buffered.
  voxtralCorrectionApiSnapshot: {
    applied: Array<{ original: string; corrected: string; position: number; confidence: number; scope: string; ts: number; stage?: string; duration_ms?: number; original_segment?: string; corrected_segment?: string; language?: string; model_id?: string }>;
    rejected: Array<{ original: string; candidate: string; reason: string; ngram: number; ts: number; stage?: string; duration_ms?: number; original_segment?: string; corrected_segment?: string; language?: string; model_id?: string; scope?: string }>;
    fetchedAt: number;
    httpStatus: 'ok' | 'error' | 'idle';
    httpError?: string;
  } = { applied: [], rejected: [], fetchedAt: 0, httpStatus: 'idle' };

  /**
   * Direct URL prefix to the Voxtral session's REST endpoints on the EC2
   * (port 80, behind the security group). Used for "Session API" links and
   * by the collapsible "Korrektur-API" panel. null when no session running.
   */
  get voxtralSessionApiBase(): string | null {
    const ip = this.broadcast?.voxtralMini4bRealtimeEc2IP;
    const sid = this.broadcast?.voxtralMini4bRealtimeSessionId;
    if (!ip || ip === 'blocked' || !sid) return null;
    return `http://${ip}/api/sessions/${sid}`;
  }


  // Voxtral Player (shares the programplan WebRTC audio element via the service singleton)
  voxtralPlayerPlaying = false;
  voxtralPlayerConnecting = false;
  voxtralPlayerActiveOtherChannel: string | null = null;
  voxtralPlayerError = '';
  private voxtralPlayerSub?: Subscription;

  // Per-segment timeline: one entry per PARTIAL / FINAL broadcast, combining
  // the post-correction text with its accepted corrections and gate-blocked
  // candidates. Populated live from the WebSocket stream; seeded once on
  // ngOnInit from GET /api/broadcast/voxtral-segments if the session is
  // already running when the dialog opens.
  voxtralSegmentTimeline: Array<{
    seq: number;
    scope: string;
    ts: number;
    audio_start?: number;
    audio_end?: number;
    text: string;
    accepted: any[];
    rejected: any[];      // Stage 1 (fuzzy) rejections only — voxtral now splits at /segments
    llm_accepted: any[];
    llm_rejected: any[];  // Stage 2 (LLM) rejections, parallel to llm_accepted
    // Frontend #1 — growing partial snapshots of the in-progress segment that
    // preceded THIS row, in chronological order (for a `partial` row, the list
    // includes earlier partials of the same group; for a `final`, it includes
    // every partial that built the finalised sentence). Mirrors voxtral-server's
    // `ctx.current_segment_partials` so the operator can spot bad corrections
    // that the LLM made off an unstable tail.
    partials?: string[];
    // 2026-09-28 — live speaker diarization for this segment, if any.
    speaker?: VoxtralSpeaker | null;
    expanded?: boolean;
  }> = [];
  voxtralSegmentScopeFilter: 'all' | 'partial' | 'final' = 'all';
  // Frontend #3 — lazy-load the post-broadcast export review panel only when
  // the operator opens it (export downloads can be hundreds of KB).
  private voxtralSegmentSeq = 0;
  // Frontend #1 — running partial buffer of the in-progress segment (cleared
  // on each FINAL). Snapshotted onto each timeline entry as `partials`.
  private voxtralCurrentSegmentPartials: string[] = [];
  readonly voxtralCorrectionContextPlaceholder = '{"type":"terms","terms":["Kiteishvili","Siebenhandl"]}';
  readonly voxtralCorrectionContextHint = 'JSON-Format: {"type":"terms","terms":[...]} oder {"type":"teams","teams":[...]}';

  constructor(
    private logEventsService: LogEventsService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private webrtcAudioService: WebrtcAudioService
  ) { }

  /** EC2 engaged (incl. the 'blocked' start-lock sentinel = EC2 starting). */
  private get voxtralEngaged(): boolean {
    return !!this.broadcast?.voxtralMini4bRealtimeEc2IP;
  }

  get stopButtonSize(): number {
    return this.isNarrow ? 120 : 160;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['broadcast']) return;
    // Keep the DDS-gate checkbox on server truth. A different broadcast in the
    // same panel instance is a hard re-seed; same broadcast only reconciles,
    // and never over an operator flip that the row has not echoed yet.
    const key = this.broadcast?.key ?? null;
    const switchedBroadcast = key !== this.seededBroadcastKey;
    this.seededBroadcastKey = key;
    this.syncIsFabDdsStartStopActiveFromRow(switchedBroadcast);
    if (switchedBroadcast) {
      // The panel instance is reused across rows — re-seed from the new row.
      this.voxtralDiarizationEnabled = this.broadcast?.voxtralMini4bRealtimeDiarizationEnabled === true;
      this.voxtralDiarizationState = null;
    }
    // Edge-triggered auto-open: fires only on the inactive→engaged transition
    // so a user who manually closed the section is not fought. The broadcast
    // reference is replaced on every live update — the guard is one compare.
    const engaged = this.voxtralEngaged;
    if (engaged && !this.lastVoxtralEngaged) {
      this.open.voxtral = true;
      if (!this.isNarrow) this.open.voxtralLive = true;
    }
    this.lastVoxtralEngaged = engaged;
  }

  ngOnInit(): void {
    this.narrowMql = window.matchMedia('(max-width: 767px)');
    this.isNarrow = this.narrowMql.matches;
    this.narrowMql.addEventListener('change', this.narrowMqlListener);
    if (this.voxtralEngaged) {
      this.open.voxtral = true;
      this.open.voxtralLive = !this.isNarrow;
    }
    this.lastVoxtralEngaged = this.voxtralEngaged;

    // 2026-06-08 — pre-fill the FAB-URL input with the channel-aware
    // default for the current environment (PRD has per-main-channel hosts;
    // DEV/STG all 10.128.16.38; regionals always carry the ?language=…
    // query string). An explicit URL persisted on the broadcast row wins;
    // we only fill when the row is blank.
    const persistedFab = (this.broadcast?.voxtralMini4bRealtimeFabUrl || '').trim();
    if (persistedFab) {
      this.voxtralFabUrl = persistedFab;
    } else {
      this.voxtralFabUrl = defaultFabUrlForChannel(this.broadcast?.channel);
    }
    // 2026-06-09 — seed voxtralFabEnabled + voxtralFabSendType from the
    // broadcast row so the FAB Aktiviert checkbox + Mode dropdown
    // reflect DDB state on dialog open. Without this the fields stayed
    // at their class-initializer values (false / "cursor") until the
    // operator clicked, so toggling + closing + reopening showed
    // unticked even when DDB had FabEnabled=true.
    this.voxtralFabEnabled = this.broadcast?.voxtralMini4bRealtimeFabEnabled === true;
    this.voxtralDiarizationEnabled = this.broadcast?.voxtralMini4bRealtimeDiarizationEnabled === true;
    // 2026-07-02 — null defaults to true (matches the backend
    // Broadcast.VoxtralMini4bRealtimeIsFabDdsStartStopActive default).
    this.seededBroadcastKey = this.broadcast?.key ?? null;
    this.syncIsFabDdsStartStopActiveFromRow(true);
    this.voxtralFabSendType = (this.broadcast?.voxtralMini4bRealtimeFabMode as string) || 'cursor';

    if (this.broadcast?.voxtralMini4bRealtimeCorrectionContext) {
      this.voxtralCorrectionContext = this.broadcast.voxtralMini4bRealtimeCorrectionContext;
    }
    // The mode enum on the broadcast; a row without one runs Phonetisch, which
    // is what the backend forwards for it (CorrectionModeCompat, 2026-10-13).
    const persistedMode = this.broadcast?.voxtralMini4bRealtimeCorrectionMode as string | null | undefined;
    if (
      persistedMode === 'off' ||
      persistedMode === 'fuzzy' ||
      persistedMode === 'phonetic'
    ) {
      this.voxtralCorrectionMode = persistedMode;
    } else if (
      persistedMode === 'llm' ||
      persistedMode === 'hybrid' ||
      persistedMode === 'fast_hybrid'
    ) {
      // Legacy LLM modes (correctors removed 2026-08) — fuzzy is the closest
      // surviving behaviour; the backend coerces the same way when it
      // forwards the persisted row to voxtral.
      this.voxtralCorrectionMode = 'fuzzy';
    } else {
      // 2026-06-04 — default new broadcasts to "phonetic" (Stage-1 Rust
      // sidecar): the lowest-latency, highest-precision name fixes.
      this.voxtralCorrectionMode = 'phonetic';
    }
    this.voxtralCorrectionEnabled = this.voxtralCorrectionMode !== 'off';

    this.logEventsSub = this.logEventsService.getLogEvents().subscribe({
      next: (logEvent: LogEvent) => {
        if (!logEvent.tags?.includes(this.broadcast.key)) return;

        // Existing live transcription lines
        if (logEvent.tags.includes('Transcription')) {
          const message = logEvent.message?.trim();
          if (message) {
            this.transcriptionLines.push(message);
            if (this.transcriptionLines.length > 10) {
              this.transcriptionLines.shift();
            }
          }
        }

        // Voxtral realtime transcription
        if (logEvent.tags.includes('VoxtralTranscription')) {
          const message = logEvent.message?.trim();
          if (!message) return;

          if (message.startsWith('SYSTEM:')) {
            // Auto-subtitle bracketing message — format "SYSTEM: [<kind>] <text>"
            // emitted by VoxtralTranscriptRelayService when voxtral-server pushes
            // a `type=system_message` frame. Render as a distinct grey banner,
            // not as a caption; do NOT push into voxtralTranscript* arrays.
            const body = message.substring('SYSTEM:'.length).trim();
            const m = body.match(/^\[(start|end|interval)\]\s*(.*)$/);
            const kind = m ? m[1] : 'system';
            const text = m ? m[2] : body;
            this.voxtralSystemMessages.unshift({ kind, text, ts: Date.now() });
            if (this.voxtralSystemMessages.length > 10) {
              this.voxtralSystemMessages.pop();
            }
          } else if (message.startsWith('PARTIAL:')) {
            const parsed = this.parseVoxtralPayload(message.substring('PARTIAL:'.length));
            this.voxtralCurrentText = parsed.text;
            this.voxtralCurrentHtml = this.renderWithCorrections(parsed.text, parsed.corrections, parsed.llm_corrections);
            this.voxtralCurrentSpeaker = parsed.speaker;
            this.recordRecentCorrections(parsed.corrections, 'partial');
            this.appendSegmentToTimeline('partial', parsed.text, parsed.corrections, parsed.rejected, parsed.llm_corrections, parsed.speaker);
            this.clearPendingIfMatched(parsed.mode);
          } else if (message.startsWith('FINAL:')) {
            const parsed = this.parseVoxtralPayload(message.substring('FINAL:'.length));
            if (parsed.text) {
              const html = this.renderWithCorrections(parsed.text, parsed.corrections, parsed.llm_corrections);
              // Parallel arrays: every unshift/pop happens together, after
              // everything that could throw has already run.
              this.voxtralTranscriptLines.unshift(parsed.text);
              this.voxtralTranscriptHtml.unshift(html);
              this.voxtralTranscriptSpeakers.unshift(parsed.speaker);
              this.voxtralCurrentText = '';
              this.voxtralCurrentHtml = '';
              this.voxtralCurrentSpeaker = null;
              if (this.voxtralTranscriptLines.length > 50) {
                this.voxtralTranscriptLines.pop();
                this.voxtralTranscriptHtml.pop();
                this.voxtralTranscriptSpeakers.pop();
              }
              this.recordRecentCorrections(parsed.corrections, 'final');
              this.appendSegmentToTimeline('final', parsed.text, parsed.corrections, parsed.rejected, parsed.llm_corrections, parsed.speaker);
              this.clearPendingIfMatched(parsed.mode);
            }
          }
        }
      }
    });

    this.timerSub = interval(1000).subscribe(() => {
      this.updateButtonState();
    });

    // Phase 4: poll the rejections channel every 10s from the running voxtral
    // session (via C# proxy). Stops fetching once the voxtral session is gone.
    this.rejectionsPollSub = interval(10000).subscribe(() => {
      this.fetchVoxtralRejections();
    });

    // Phase 3 (Option A): one-shot seed the per-segment timeline from the
    // backend if the session is already mid-run when the dialog opens. Live
    // updates flow via the WebSocket path above.
    this.seedVoxtralSegmentTimeline();

    // Player state — only "playing" when the shared WebRTC stream is on this
    // broadcast's channel. A different channel still in playback exposes its
    // name so the UI can offer "Aktuell läuft: ORF-1 → switch to this".
    this.voxtralPlayerSub = this.webrtcAudioService.connectionState$.subscribe((state: string) => {
      const activeName = this.webrtcAudioService.currentChannelName;
      const myChannel = activeName === this.broadcast?.channel;
      const isLive = state === 'connecting' || state === 'connected';
      this.voxtralPlayerPlaying = state === 'connected' && myChannel;
      this.voxtralPlayerConnecting = state === 'connecting' && myChannel;
      this.voxtralPlayerActiveOtherChannel = isLive && !myChannel ? activeName : null;
    });
  }

  private fetchVoxtralRejections(): void {
    if (!this.broadcast?.key || !this.broadcast?.voxtralMini4bRealtimeEc2IP) {
      return;
    }
    const url = `${environment.API_Base}/broadcast/voxtral-corrections?broadcastKey=${encodeURIComponent(this.broadcast.key)}&limit=100`;
    this.http.get<any>(url).subscribe({
      next: (resp) => {
        const rej = Array.isArray(resp?.rejected) ? resp.rejected : [];
        this.voxtralRecentRejections = rej
          .slice(0, 100)
          .map((r: any) => ({
            original: r.original || '',
            candidate: r.candidate || '',
            reason: r.reason || '',
            ngram: r.ngram || 1,
            ts: (r.ts || 0) * 1000, // backend sends seconds
            stage: r.stage || 'fuzzy',
            duration_ms: typeof r.duration_ms === 'number' ? r.duration_ms : 0,
            original_segment: r.original_segment || '',
            corrected_segment: r.corrected_segment || '',
            language: r.language || '',
            model_id: r.model_id || '',
            scope: r.scope || '',
          }));

        // Mirror the full payload into the API-panel snapshot. The panel
        // intentionally shows raw seconds-since-epoch timestamps and the
        // server-supplied `stage` chip so operators can distinguish what
        // the backend has *buffered* from what the WS has *streamed*.
        const ts = Date.now();
        const mapApplied = (arr: any[]) => arr.map((c: any) => ({
          original: c.original || '',
          corrected: c.corrected || '',
          position: typeof c.position === 'number' ? c.position : 0,
          confidence: typeof c.confidence === 'number' ? c.confidence : 0,
          scope: c.scope || 'final',
          ts: (c.ts || 0) * 1000,
          // Prefer the server-supplied stage. Older backends (pre-stage roll)
          // omit it for Stage-1 rows; fall back to the broadcast's correction
          // mode so a `phonetic` session is labelled "Phonetisch" instead of
          // the historical hardcoded "Fuzzy". Non-phonetic Stage-1 stays fuzzy.
          stage: c.stage || (this.voxtralCorrectionMode === 'phonetic' ? 'phonetic' : 'fuzzy'),
          duration_ms: typeof c.duration_ms === 'number' ? c.duration_ms : 0,
          original_segment: c.original_segment || '',
          corrected_segment: c.corrected_segment || '',
          language: c.language || '',
          model_id: c.model_id || '',
        }));
        const mapRejected = (arr: any[]) => arr.map((r: any) => ({
          original: r.original || '',
          candidate: r.candidate || '',
          reason: r.reason || '',
          ngram: r.ngram || 1,
          ts: (r.ts || 0) * 1000,
          stage: r.stage || 'fuzzy',
          duration_ms: typeof r.duration_ms === 'number' ? r.duration_ms : 0,
          original_segment: r.original_segment || '',
          corrected_segment: r.corrected_segment || '',
          language: r.language || '',
          model_id: r.model_id || '',
          scope: r.scope || '',
        }));
        this.voxtralCorrectionApiSnapshot = {
          applied: mapApplied(Array.isArray(resp?.applied) ? resp.applied : []),
          rejected: mapRejected(rej),
          fetchedAt: ts,
          httpStatus: 'ok',
        };
      },
      error: (err: any) => {
        this.voxtralCorrectionApiSnapshot = {
          ...this.voxtralCorrectionApiSnapshot,
          fetchedAt: Date.now(),
          httpStatus: 'error',
          httpError: typeof err?.error === 'string' ? err.error : (err?.message || 'unknown'),
        };
      }
    });
  }


  updateButtonState() {
    // zum Testen vorerst immer aktiv
    this.isActive = true;

    /*
    if (!this.broadcast?.startTime || !this.broadcast?.bruttoDuration) {
      this.isActive = false;
      return;
    }

    const now = Date.now();
    const start = new Date(this.broadcast.startTime).getTime();
    const end = start + this.parseISODuration(this.broadcast.bruttoDuration);

    this.isActive = now >= start && now <= end;
    */
  }

  ngOnDestroy() {
    this.narrowMql?.removeEventListener('change', this.narrowMqlListener);
    this.timerSub.unsubscribe();
    this.logEventsSub.unsubscribe();
    this.rejectionsPollSub?.unsubscribe();
    this.voxtralPlayerSub?.unsubscribe();
  }

  async toggleVoxtralPlayer(): Promise<void> {
    this.voxtralPlayerError = '';
    if (this.voxtralPlayerPlaying) {
      this.webrtcAudioService.disconnect();
      return;
    }
    if (!this.broadcast?.channel) {
      this.voxtralPlayerError = 'Sender unbekannt.';
      return;
    }
    try {
      const sessions = await this.webrtcAudioService.fetchSessions();
      const session = sessions.find(s => s.name === this.broadcast.channel);
      if (!session) {
        this.voxtralPlayerError = `Kein Live-Player für ${this.broadcast.channel} verfügbar.`;
        return;
      }
      // audioEl is undefined on purpose — reuse the element registered by the
      // programplan view so we drive the same <audio> tag the operator sees.
      await this.webrtcAudioService.connect(session.sessionId, undefined, session.name);
    } catch (err) {
      console.error('[Details] WebRTC connect failed:', err);
      this.voxtralPlayerError = 'Player konnte nicht gestartet werden.';
    }
  }

  activateLiveTranscription(broadcast: Broadcast) {
    this.isProcessing = true;
    this.http.post(environment.API_Base + '/broadcast/activate', { broadcastKey: broadcast.key })
      .subscribe({
        next: response => {
          console.log('Transkription gestartet:', response);
        },
        error: error => {
          console.error('Fehler beim Starten:', error);
          // Optional: Fehlermeldung anzeigen
        },
        complete: () => {
          this.isProcessing = false;
        }
      });
  }

  removeLiveTranscription(broadcast: Broadcast) {
    this.isProcessing = true;
    this.http.post(environment.API_Base + '/broadcast/remove', { broadcastKey: broadcast.key })
      .subscribe({
        next: response => {
          console.log('Transkription gestoppt:', response);
        },
        error: error => {
          console.error('Fehler beim Stoppen:', error);
        },
        complete: () => {
          this.isProcessing = false;
        }
      });
  }

  parseISODuration(duration: string): number {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const match = duration.match(regex);
    if (!match) return 0;

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);

    return ((hours * 60 + minutes) * 60 + seconds) * 1000; // in ms
  }

  recordingStarted(broadcast: Broadcast): boolean {
    return broadcast.recordingStartedAt !== null && !broadcast.recordingStoppedAt;
  }

  openLogs() {
    if (!this.broadcast?.key) {
      return;
    }

    this.showLogsModal = true;
    this.isLoadingLogs = true;

    this.getLogsByKey(this.broadcast.key).subscribe({
      next: (result) => {
        this.logs = result
          .sort((a, b) => {
            const dateA = new Date(a.Timestamp).getTime();
            const dateB = new Date(b.Timestamp).getTime();
            return dateB - dateA; // absteigend
          })
          .map(log => {
            let innerLog: any;
            try {
              innerLog = JSON.parse(log.message);
            } catch (err) {
              innerLog = { Timestamp: '', Level: '', Message: log.message };
            }

            let messageText = innerLog.Message || log.message;

            // Regex für ISO 8601 Timestamps mit optional Millisekunden
            const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;

            messageText = messageText.replace(isoRegex, (match: string) => {
              const date = new Date(match); // UTC wird automatisch erkannt
              return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            });

            let localTime = '';
            if (innerLog.Timestamp) {
              const date = new Date(innerLog.Timestamp);
              localTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }

            return `${localTime} ${messageText}`;
          });

        this.isLoadingLogs = false;
      },
      error: (err) => {
        console.error('Fehler beim Laden der Logs', err);
        this.logs = ['Fehler beim Laden der Logs'];
        this.isLoadingLogs = false;
      }
    });
  }

  getLogsByKey(key: string): Observable<any[]> {
    return this.http.get<any[]>(environment.API_Base + '/CloudWatch', {
      params: { tag: key }
    });
  }


  async onKiutToggle(checked: boolean) {
    // Optimistic update
    const prev = this.broadcast.kiut;
    this.savingKiut = true;
    try {
      this.http.post(environment.API_Base + '/broadcast/enable-kiut', { broadcastKey: this.broadcast.key, EnableKiut: checked })
        .subscribe({
          next: response => {
            console.log('KIUT-Flag gesetzt:', response);
          },
          error: error => {
            console.error('Fehler beim Setzen KIUT-Flag:', error);
          },
          complete: () => {
            this.savingKiut = false;
          }
        });
    } catch (err) {
      // Rollback bei Fehler
      this.broadcast.kiut = prev;
      // optional: Nutzer informieren
      console.error('Update KIUT fehlgeschlagen', err);
    } finally {
      this.savingKiut = false;
    }
  }

  onLiveStreamSourceChange(value: string) {
    const prev = this.broadcast.liveStreamSource;
    this.broadcast.liveStreamSource = value;
    this.savingLiveStreamSource = true;
    this.http.post(environment.API_Base + '/broadcast/update-livestreamsource', {
      broadcastKey: this.broadcast.key,
      liveStreamSource: value
    }).subscribe({
      next: () => {
        console.log('LiveStreamSource gesetzt:', value);
      },
      error: (error) => {
        console.error('Fehler beim Setzen der LiveStreamSource:', error);
        this.broadcast.liveStreamSource = prev;
      },
      complete: () => {
        this.savingLiveStreamSource = false;
      }
    });
  }

  async stopRecording() {
    // Optimistic update
    const prev = this.broadcast.kiut;
    this.savingKiut = true;
    try {
      this.http.post(environment.API_Base + '/broadcast/stop-recording', { broadcastKey: this.broadcast.key })
        .subscribe({
          next: response => {
            console.log('KIUT-Flag gesetzt:', response);
          },
          error: error => {
            console.error('Fehler beim Setzen KIUT-Flag:', error);
          },
          complete: () => {
            this.savingKiut = false;
          }
        });
    } catch (err) {
      // Rollback bei Fehler
      this.broadcast.kiut = prev;
      // optional: Nutzer informieren
      console.error('Update KIUT fehlgeschlagen', err);
    } finally {
      this.savingKiut = false;
    }
  }

  async reinit() {
    const confirmed = confirm('Soll der Neustart wirklich ausgeführt werden? Dieser Vorgang dauert ein paar Minuten.');
    if (!confirmed) {
      console.log('Neustart abgebrochen.');
      return;
    }

    try {
      this.isProcessing = true;
      this.http.post(environment.API_Base + '/broadcast/reinit', { broadcastKey: this.broadcast.key })
        .pipe(finalize(() => this.isProcessing = false))
        .subscribe({
          next: response => {
            console.log('Neustart:', response);
          },
          error: error => {
            console.error('Fehler beim Neustart:', error);
          },
        });
    } catch (err) {
      console.error('Neustart fehlgeschlagen', err);
    }
  }

  beginEditStartTime() {
    this.startTimeInput = this.dateToHms(new Date(this.broadcast.startTime));
    this.editingStartTime = true;
  }

  cancelEditStartTime() {
    this.editingStartTime = false;
  }

  async saveStartTime() {
    try {
      const newStart = this.mergeTimeIntoDate(this.broadcast.startTime, this.startTimeInput);
      // Optimistic UI
      const prev = this.broadcast.startTime;
      this.broadcast.startTime = newStart;
      this.editingStartTime = false;

      await this.http.post(
        environment.API_Base + '/broadcast/update-start',
        { broadcastKey: this.broadcast.key, startTime: newStart }
      ).toPromise();
    } catch (e) {
      console.error('Startzeit speichern fehlgeschlagen', e);
      // optional: Toast
    }
  }

  beginEditDuration() {
    this.durationInput = this.isoDurationToHms(this.broadcast.bruttoDuration);
    this.editingDuration = true;
  }

  cancelEditDuration() {
    this.editingDuration = false;
  }

  async saveDuration() {
    try {
      const iso = this.hmsToIsoDuration(this.durationInput);
      // Optimistic UI
      const prev = this.broadcast.bruttoDuration;
      this.broadcast.bruttoDuration = iso;
      this.editingDuration = false;

      await this.http.post(
        environment.API_Base + '/broadcast/update-duration',
        { broadcastKey: this.broadcast.key, bruttoDuration: iso }
      ).toPromise();
    } catch (e) {
      console.error('Dauer speichern fehlgeschlagen', e);
      // optional: Toast
    }
  }

  // --- Helpers ---
  private pad2(n: number) { return n.toString().padStart(2, '0'); }

  private dateToHms(d: Date): string {
    return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())}:${this.pad2(d.getSeconds())}`;
  }

  /**
   * Nimmt vorhandenes Datum (broadcast.startTime) und ersetzt nur die Uhrzeit
   * @param dateOrIso: vorhandene Startzeit (Date|string)
   * @param hms: "HH:mm:ss"
   * @returns ISO-String
   */
  private mergeTimeIntoDate(dateOrIso: string | Date, hms: string): string {
    const base = new Date(dateOrIso);
    const [hh, mm, ss] = hms.split(':').map(x => parseInt(x, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm) || Number.isNaN(ss)) {
      throw new Error('Ungültige Startzeit (erwartet HH:mm:ss)');
    }
    base.setHours(hh, mm, ss, 0);
    return base.toISOString();
  }

  /** "PT#H#M#S" -> "HH:mm:ss" */
  private isoDurationToHms(iso: string): string {
    const m = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso || '');
    const h = parseInt(m?.[1] ?? '0', 10);
    const mi = parseInt(m?.[2] ?? '0', 10);
    const s = parseInt(m?.[3] ?? '0', 10);
    return `${this.pad2(h)}:${this.pad2(mi)}:${this.pad2(s)}`;
  }

  /** "HH:mm:ss" -> "PT#H#M#S" */
  private hmsToIsoDuration(hms: string): string {
    const m = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(hms.trim());
    if (!m) throw new Error('Ungültige Dauer (erwartet HH:mm:ss)');
    const h = parseInt(m[1], 10);
    const mi = parseInt(m[2], 10);
    const s = parseInt(m[3], 10);
    return `PT${h ? h + 'H' : ''}${mi ? mi + 'M' : ''}${s ? s + 'S' : '0S'}`;
  }

  beginEditRedsysSendungsname() {
    this.redsysNameInput = this.broadcast.redsysSendungsname ?? '';
    this.editingRedsysSendungsname = true;
  }

  cancelEditRedsysSendungsname() {
    this.editingRedsysSendungsname = false;
  }

  async saveRedsysSendungsname() {
    try {
      const newName = (this.redsysNameInput ?? '').trim();

      // Optimistic UI
      const prev = this.broadcast.redsysSendungsname;
      this.broadcast.redsysSendungsname = newName;
      this.editingRedsysSendungsname = false;

      await this.http.post(
        environment.API_Base + '/broadcast/update-redsys-name',
        { broadcastKey: this.broadcast.key, redsysSendungsname: newName }
      ).toPromise();
    } catch (e) {
      console.error('RedsysSendungsname speichern fehlgeschlagen', e);
      // optional: Toast
    }
  }

  async fetchStartDurationFromRedsysAsync() {
    try {
      console.log('Redsys Startzeit und Dauer abfragen für Broadcast:', this.broadcast.key);
      await this.http.post(
        environment.API_Base + '/broadcast/fetch-redsys-start-duration',
        { broadcastKey: this.broadcast.key }
      ).toPromise();
    } catch (e) {
      console.error('RedsysSendungsname speichern fehlgeschlagen', e);
      // optional: Toast
    }
  }

  // --- Voxtral Mini 4B Realtime ---
  voxtralResetting = false;
  voxtralTerminating = false;
  voxtralScheduleToggling = false;
  voxtralFabEnabledSaving = false;

  /**
   * Persist the FAB-enabled toggle to the broadcast row immediately on change.
   * The previous flow only persisted FAB state at Start; operators expected the
   * checkbox to be sticky across navigation.
   */
  onVoxtralFabEnabledChanged(value: boolean) {
    if (this.voxtralFabEnabledSaving) return;
    if (!this.broadcast?.key) return;

    // 2026-09-02 — same reasoning as onVoxtralIsFabDdsStartStopActiveChanged:
    // the checkbox only emits on a real flip, and the row can lag our own POST
    // by a round-trip. Reading `previous` off the row let a re-toggle collapse
    // into a no-op, leaving DDB on the opposite of what the operator sees.
    const previous = !value;

    this.voxtralFabEnabledSaving = true;
    this.broadcast.voxtralMini4bRealtimeFabEnabled = value;
    this.voxtralError = '';

    const sessionRunning = !!this.broadcast?.voxtralMini4bRealtimeEc2IP
      && this.broadcast.voxtralMini4bRealtimeEc2IP !== 'blocked';

    // 2026-06-09 — every FAB toggle (on AND off) also re-asserts the
    // FAB URL on the row. Same intent as the schedule-dialog
    // onFabToggle path: lock the channel-correct URL into DDB so a
    // legacy/stale value (e.g. PRD ORF1's 10.128.16.38 instead of the
    // channel-correct 10.128.16.34) isn't left in place when an
    // operator clicks the enable toggle. Empty row → channel default;
    // non-empty → re-write the same value. Fire-and-forget — failure
    // doesn't undo the enable toggle.
    const existingFab = (this.voxtralFabUrl || '').trim()
      || (this.broadcast?.voxtralMini4bRealtimeFabUrl as string || '').trim();
    const targetFab = existingFab || defaultFabUrlForChannel(this.broadcast?.channel);
    if (targetFab) {
      this._forcePersistFabUrl(targetFab);
    }

    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-fab-enabled', {
      broadcastKey: this.broadcast.key,
      fabEnabled: value
    }).pipe(
      finalize(() => this.voxtralFabEnabledSaving = false)
    ).subscribe({
      next: (resp: any) => {
        // Show a brief "wird angewendet" badge when the live update is in
        // flight against a running session. Sub-second apply on Voxtral side
        // means a fixed short timer is good enough — no WS handshake needed.
        if (sessionRunning && resp?.live === true) {
          this.voxtralFabPending = true;
          this.voxtralFabPendingTarget = value;
          setTimeout(() => {
            if (this.voxtralFabPendingTarget === value) {
              this.voxtralFabPending = false;
              this.voxtralFabPendingTarget = null;
            }
          }, 1500);
        } else if (sessionRunning && resp?.live === false) {
          this.voxtralError = 'Live-FAB-Aktualisierung fehlgeschlagen — wird beim nächsten Start angewendet.';
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Speichern des FAB-Flags:', httpError);
        this.broadcast.voxtralMini4bRealtimeFabEnabled = previous;
        this.voxtralFabEnabled = previous;
        this.voxtralFabPending = false;
        this.voxtralFabPendingTarget = null;
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'FAB-Aktivierung konnte nicht gespeichert werden.';
      }
    });
  }

  /**
   * 2026-09-28 — per-broadcast live speaker diarization. Persists the row
   * flag and live-pushes it to a running session (same shape as the FAB
   * toggle). Speaker labels only ever show up here in the portal — FAB never
   * receives them. `voxtralDiarizationState` keeps voxtral's answer so an EC2
   * without the diarizer (older AMI) reads "nicht verfügbar".
   */
  onVoxtralDiarizationEnabledChanged(value: boolean) {
    if (this.voxtralDiarizationSaving) return;
    if (!this.broadcast?.key) return;
    const previous = !value;

    this.voxtralDiarizationSaving = true;
    this.broadcast.voxtralMini4bRealtimeDiarizationEnabled = value;
    this.voxtralError = '';

    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-diarization-enabled', {
      broadcastKey: this.broadcast.key,
      diarizationEnabled: value
    }).pipe(
      finalize(() => this.voxtralDiarizationSaving = false)
    ).subscribe({
      next: (resp: any) => {
        this.voxtralDiarizationState = typeof resp?.diarizationState === 'string' ? resp.diarizationState : null;
        if (resp?.live === true) {
          this.voxtralDiarizationPending = true;
          setTimeout(() => this.voxtralDiarizationPending = false, 1500);
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Speichern des Diarisierungs-Flags:', httpError);
        this.broadcast.voxtralMini4bRealtimeDiarizationEnabled = previous;
        this.voxtralDiarizationEnabled = previous;
        this.voxtralDiarizationPending = false;
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Sprecher-Diarisierung konnte nicht gespeichert werden.';
      }
    });
  }

  /**
   * 2026-10-09 — saves the session's unified export (segments, corrections,
   * prompts and, when it ran, speaker diarization) as a JSON file. Same
   * endpoint as the export review: live from the voxtral-server while the
   * session runs, otherwise the brotli mirror in S3, decompressed by the
   * backend. Fetched as a Blob so a large export is never parsed here.
   */
  downloadVoxtralExport() {
    const key = this.broadcast?.key;
    if (!key || this.voxtralExportDownloading) return;

    this.voxtralExportDownloading = true;
    this.voxtralError = '';
    const url = `${environment.API_Base}/broadcast/voxtral-segments-export?broadcastKey=${encodeURIComponent(key)}`;
    this.http.get(url, { responseType: 'blob' }).pipe(
      finalize(() => this.voxtralExportDownloading = false)
    ).subscribe({
      next: (blob: Blob) => downloadBlob(voxtralExportFileName(key), blob),
      error: (httpError: any) => {
        console.error('Fehler beim Herunterladen des Voxtral-Transkripts:', httpError);
        this.voxtralError = 'Transkript konnte nicht heruntergeladen werden.';
      }
    });
  }

  /**
   * 2026-07-02 — persist the per-broadcast IsFabDdsStartStopActive toggle
   * and immediately live-push to the running voxtral session. Mirror of
   * `onVoxtralFabEnabledChanged` shape (spinner + pending badge + rollback).
   * Checked (true) → voxtral's FAB gate follows the DDS intervals list;
   * unchecked (false) → gate is bypassed entirely.
   */
  onVoxtralIsFabDdsStartStopActiveChanged(value: boolean) {
    if (this.voxtralIsFabDdsStartStopActiveSaving) return;
    if (!this.broadcast?.key) return;
    // 2026-09-02 — the state we are leaving is `!value`: ngModelChange only
    // fires on a real view flip. This used to read the broadcast row instead,
    // which made re-arming the gate a SILENT no-op whenever the row lagged or
    // (until the GraphQL selection set was fixed) never carried the field at
    // all: `previous` came out true, `value === previous` returned early, no
    // POST was sent, and DDB kept the opposite of what the checkbox showed.
    const previous = !value;

    this.voxtralIsFabDdsStartStopActiveSaving = true;
    this.fabDdsGateDesired = value;
    this.fabDdsGateDesiredSince = Date.now();
    (this.broadcast as any).voxtralMini4bRealtimeIsFabDdsStartStopActive = value;
    this.voxtralError = '';

    const sessionRunning = !!this.broadcast?.voxtralMini4bRealtimeEc2IP
      && this.broadcast.voxtralMini4bRealtimeEc2IP !== 'blocked';

    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-fab-dds-start-stop-active', {
      broadcastKey: this.broadcast.key,
      isFabDdsStartStopActive: value,
    }).pipe(
      finalize(() => this.voxtralIsFabDdsStartStopActiveSaving = false)
    ).subscribe({
      next: (resp: any) => {
        if (sessionRunning && resp?.live === true) {
          this.voxtralIsFabDdsStartStopActivePending = true;
          this.voxtralIsFabDdsStartStopActivePendingTarget = value;
          setTimeout(() => {
            if (this.voxtralIsFabDdsStartStopActivePendingTarget === value) {
              this.voxtralIsFabDdsStartStopActivePending = false;
              this.voxtralIsFabDdsStartStopActivePendingTarget = null;
            }
          }, 1500);
        } else if (sessionRunning && resp?.live === false) {
          this.voxtralError = 'Live-Aktualisierung fehlgeschlagen — wird beim nächsten Start angewendet.';
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Speichern des DDS-FAB-Gate-Flags:', httpError);
        (this.broadcast as any).voxtralMini4bRealtimeIsFabDdsStartStopActive = previous;
        this.voxtralIsFabDdsStartStopActive = previous;
        this.fabDdsGateDesired = null;
        this.voxtralIsFabDdsStartStopActivePending = false;
        this.voxtralIsFabDdsStartStopActivePendingTarget = null;
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'DDS-FAB-Gate konnte nicht gespeichert werden.';
      }
    });
  }

  /**
   * The persisted DDS-gate flag as carried by the broadcast row.
   *
   * `undefined` means the payload did not carry the field at all (a client or
   * cached response predating the 2026-09-02 selection-set fix) — that is
   * "unknown", NOT "true", and must never be turned into a checkbox state.
   * `null` means no operator ever set it → the backend default, true.
   */
  private rowIsFabDdsStartStopActive(): boolean | undefined {
    const raw = (this.broadcast as any)?.voxtralMini4bRealtimeIsFabDdsStartStopActive;
    return raw === undefined ? undefined : raw !== false;
  }

  /** Keep the "FAB nach DDS" checkbox on server truth — see syncFabDdsGate(). */
  private syncIsFabDdsStartStopActiveFromRow(force: boolean): void {
    const next = syncFabDdsGate({
      rowValue: this.rowIsFabDdsStartStopActive(),
      current: this.voxtralIsFabDdsStartStopActive,
      desired: this.fabDdsGateDesired,
      desiredSince: this.fabDdsGateDesiredSince,
      now: Date.now(),
      saving: this.voxtralIsFabDdsStartStopActiveSaving,
      force,
    });
    this.voxtralIsFabDdsStartStopActive = next.value;
    this.fabDdsGateDesired = next.desired;
  }

  /**
   * 2026-08-04 — when will FAB start receiving, given the DDS gate?
   *
   * Returns the DDS start time while the gate is closed *because the window is
   * still ahead*, else null. Mirrors fab_forwarder.describe_gate: the gate only
   * applies while both the FAB toggle and "FAB nach DDS" are on.
   *
   * Deliberately computed from the broadcast row rather than the running
   * session, so the warning is already visible when the operator schedules or
   * hand-starts a session — which is exactly when it is actionable.
   */
  fabGateClosedUntil(): Date | null {
    if (!this.voxtralFabEnabled) return null;
    if (!this.voxtralIsFabDdsStartStopActive) return null;
    const startsAt = this.broadcast?.ddsStartsAt;
    if (!startsAt) return null;
    const start = new Date(startsAt);
    if (isNaN(start.getTime())) return null;
    return start.getTime() > Date.now() ? start : null;
  }

  /**
   * 2026-08-04 — clears a wrong "Vortranskribiert" flag so the live KIUT
   * pipelines see the broadcast again.
   *
   * IsPretranscribed=true means "a prepared UT file exists in der UTDB", and it
   * locks the broadcast out of BroadcastScheduleService, LiveTranscriptionService
   * and DDSReflagDecision alike. The operator's fix is to delete the file in der
   * UTDB, but that only feeds back into the row on the next PcmsDownloadJob tick
   * (up to 30 minutes) — far too slow for a running broadcast, and on the ORFS
   * outage 2026-08-04 it never happened at all. This does the reset directly.
   */
  onResetPretranscribed() {
    if (this.resetPretranscribedSaving) return;
    if (!this.broadcast?.key) return;

    this.resetPretranscribedSaving = true;
    this.resetPretranscribedError = '';

    this.http.post<any>(environment.API_Base + '/broadcast/reset-pretranscribed', {
      broadcastKey: this.broadcast.key,
    }).pipe(
      finalize(() => this.resetPretranscribedSaving = false)
    ).subscribe({
      next: () => {
        // Optimistic local update; the GraphQL subscription delivers the
        // authoritative row shortly after.
        if (this.broadcast) {
          this.broadcast.isPretranscribed = false;
          (this.broadcast as any).isPcmsDownloaded = false;
          (this.broadcast as any).pcmsFabStlUploadDate = null;
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Zurücksetzen des Vortranskribiert-Flags:', httpError);
        const serverMsg = httpError?.error;
        this.resetPretranscribedError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Live-KIUT konnte nicht freigegeben werden.';
      }
    });
  }

  /**
   * Channel-aware FAB URL placeholder/fallback for the template. Used
   * by the FAB URL <input> as a placeholder so operators see the per-
   * env default for this broadcast's channel instead of a hard-coded
   * 10.128.16.38 literal.
   */
  defaultFabUrlForCurrentChannel(): string {
    return defaultFabUrlForChannel(this.broadcast?.channel);
  }

  /**
   * 2026-06-09 — fire-and-forget POST /broadcast/voxtral-fab-url with
   * no idempotency short-circuit. Called from onVoxtralFabEnabledChanged
   * on every toggle so the row's URL is committed to DDB whether it
   * matches the local state or not. Independent of the debounced
   * keystroke handler `_persistFabUrl` so the spinner / pending badge
   * keep their meaning (URL was edited vs URL was re-asserted by a
   * toggle). Failure surfaces in voxtralError but does NOT revert the
   * enable toggle the operator just clicked.
   */
  private _forcePersistFabUrl(value: string) {
    if (!this.broadcast?.key) return;
    if (this.voxtralFabUrlSaving) return;
    this.voxtralFabUrlSaving = true;
    this.broadcast.voxtralMini4bRealtimeFabUrl = value;
    this.voxtralFabUrl = value;
    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-fab-url', {
      broadcastKey: this.broadcast.key,
      fabUrl: value,
    }).pipe(
      finalize(() => this.voxtralFabUrlSaving = false)
    ).subscribe({
      next: () => { /* no spinner needed — toggle UX owns the user feedback */ },
      error: (httpError: any) => {
        console.error('Force-persist FAB URL failed:', httpError);
        // Don't reset the URL — leave it in the row so the operator can
        // re-attempt by editing the input.
      },
    });
  }

  /**
   * 2026-06-08 — persist the FAB-URL change on the broadcast row AND live-
   * update the running voxtral session (the forwarder applies
   * pending_fab_url at the next tick, so the swap takes effect in
   * milliseconds). Debounced from the input's (ngModelChange) so a
   * keystroke storm doesn't fan out into a request per character.
   */
  onVoxtralFabUrlChanged(value: string) {
    const trimmed = (value ?? '').trim();
    // Always clear an existing debounce so the latest keystroke wins.
    if (this.voxtralFabUrlDebounceHandle) {
      clearTimeout(this.voxtralFabUrlDebounceHandle);
      this.voxtralFabUrlDebounceHandle = null;
    }
    if (!trimmed) return;  // empty value is a no-op (operator clearing the field)
    if (!this.broadcast?.key) return;
    this.voxtralFabUrlDebounceHandle = setTimeout(() => this._persistFabUrl(trimmed), 600);
  }

  private _persistFabUrl(value: string) {
    if (this.voxtralFabUrlSaving) return;
    const previous = (this.broadcast?.voxtralMini4bRealtimeFabUrl as string) || '';
    if (value === previous) return;

    this.voxtralFabUrlSaving = true;
    this.broadcast.voxtralMini4bRealtimeFabUrl = value;
    this.voxtralError = '';

    const sessionRunning = !!this.broadcast?.voxtralMini4bRealtimeEc2IP
      && this.broadcast.voxtralMini4bRealtimeEc2IP !== 'blocked';

    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-fab-url', {
      broadcastKey: this.broadcast.key,
      fabUrl: value,
    }).pipe(
      finalize(() => this.voxtralFabUrlSaving = false)
    ).subscribe({
      next: (resp: any) => {
        if (sessionRunning && resp?.live === true) {
          this.voxtralFabUrlPending = true;
          this.voxtralFabUrlPendingTarget = value;
          setTimeout(() => {
            if (this.voxtralFabUrlPendingTarget === value) {
              this.voxtralFabUrlPending = false;
              this.voxtralFabUrlPendingTarget = null;
            }
          }, 1500);
        } else if (sessionRunning && resp?.live === false) {
          this.voxtralError = 'Live-FAB-URL-Aktualisierung fehlgeschlagen — wird beim nächsten Start angewendet.';
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Speichern der FAB-URL:', httpError);
        this.broadcast.voxtralMini4bRealtimeFabUrl = previous;
        this.voxtralFabUrl = previous;
        this.voxtralFabUrlPending = false;
        this.voxtralFabUrlPendingTarget = null;
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'FAB-URL konnte nicht gespeichert werden.';
      }
    });
  }

  toggleVoxtralSchedule() {
    if (this.voxtralScheduleToggling) return;
    if (!this.broadcast?.key) return;

    const previous = !!this.broadcast.voxtralMini4bRealtimeScheduled;
    const target = !previous;
    this.broadcast.voxtralMini4bRealtimeScheduled = target;
    this.voxtralScheduleToggling = true;
    this.voxtralError = '';

    const url = environment.API_Base + '/voxtral-schedule/' + encodeURIComponent(this.broadcast.key);
    const request$ = target ? this.http.post(url, {}) : this.http.delete(url);

    request$.pipe(
      finalize(() => this.voxtralScheduleToggling = false)
    ).subscribe({
      next: () => {
        if (target) {
          // ScheduleAsync clears the operator-stop veto server-side so the
          // reconciler can manage subsequent runs; reflect that in the UI.
          this.broadcast.voxtralMini4bRealtimeOperatorStoppedAt = undefined;
        }
      },
      error: (httpError: any) => {
        console.error('Fehler beim Planen/Aufheben der Voxtral-Sendung:', httpError);
        this.broadcast.voxtralMini4bRealtimeScheduled = previous;
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Voxtral-Planung konnte nicht aktualisiert werden.';
      }
    });
  }

  startVoxtral() {
    this.voxtralStarting = true;
    this.voxtralError = '';
    this.http.post(environment.API_Base + '/broadcast/voxtral-start', {
      broadcastKey: this.broadcast.key,
      fabEnabled: this.voxtralFabEnabled,
      fabSendType: this.voxtralFabSendType,
      fabUrl: this.voxtralFabEnabled ? this.voxtralFabUrl : '',
      language: 'de',
      correctionContext: this.voxtralCorrectionContext || null,
      correctionEnabled: this.voxtralCorrectionEnabled
    }).pipe(
      finalize(() => this.voxtralStarting = false)
    ).subscribe({
      next: () => {
        // Backend returns immediately. The UI updates when voxtralMini4bRealtimeEc2IP
        // changes via GraphQL subscription (blocked -> IP).
      },
      error: (httpError: any) => {
        console.error('Fehler beim Starten von Voxtral:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Fehler beim Starten der Voxtral-Transkription.';
      }
    });
  }

  stopVoxtral() {
    this.voxtralStopping = true;
    this.voxtralError = '';
    this.http.post(environment.API_Base + '/broadcast/voxtral-stop', {
      broadcastKey: this.broadcast.key
    }).pipe(
      finalize(() => this.voxtralStopping = false)
    ).subscribe({
      next: () => {
        this.voxtralCurrentText = '';
        this.voxtralCurrentHtml = '';
        this.voxtralTranscriptLines = [];
        this.voxtralTranscriptHtml = [];
        this.voxtralTranscriptSpeakers = [];
        this.voxtralCurrentSpeaker = null;
        this.voxtralRecentCorrections = [];
        this.voxtralRecentRejections = [];
        this.voxtralSegmentTimeline = [];
        this.voxtralSegmentSeq = 0;
        this.voxtralCurrentSegmentPartials = [];
        this.voxtralSystemMessages = [];
      },
      error: (httpError: any) => {
        console.error('Fehler beim Stoppen von Voxtral:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Fehler beim Stoppen der Voxtral-Transkription.';
      }
    });
  }

  /**
   * F3 admin panel: accumulate each incoming correction into a rolling list.
   * Deduplicates adjacent identical entries (same original→corrected) so a
   * burst of partials doesn't flood the panel with the same pair.
   */
  private recordRecentCorrections(corrections: any[], scope: string): void {
    if (!corrections || corrections.length === 0) return;
    const now = Date.now();
    const last = this.voxtralRecentCorrections[0];
    for (const c of corrections) {
      if (!c || !c.original || !c.corrected) continue;
      if (last && last.original === c.original && last.corrected === c.corrected && (now - last.ts) < 3000) {
        continue; // dedupe near-repeats within 3s
      }
      this.voxtralRecentCorrections.unshift({
        original: c.original,
        corrected: c.corrected,
        ts: now,
        scope,
      });
    }
    if (this.voxtralRecentCorrections.length > 100) {
      this.voxtralRecentCorrections.length = 100;
    }
  }

  /**
   * Clears the "Wird angewendet ab nächstem Satzende" badge when the WS payload
   * echoes a correction_mode that matches what we just requested. Called from
   * the PARTIAL/FINAL handlers — first matching FINAL flips the flag off.
   */
  private clearPendingIfMatched(mode: string | null | undefined): void {
    if (this.voxtralCorrectionPending && mode && this.voxtralCorrectionPendingTarget === mode) {
      this.voxtralCorrectionPending = false;
      this.voxtralCorrectionPendingTarget = null;
    }
  }

  /**
   * Live correction mode change. Persists the new mode on the broadcast (so a
   * fresh start picks it up) and, if a session is already running, sends a
   * live update to Voxtral that lands at the next sentence FINAL boundary.
   * Optimistic UI: flips the local field immediately, reverts on error.
   *
   * Accepts the four-option enum directly. The new `<select>` calls this;
   * the back-compat boolean helper below routes legacy callers through here.
   */
  onCorrectionModeChange(target: 'off' | 'fuzzy' | 'phonetic'): void {
    const previousMode = this.voxtralCorrectionMode;
    const previousEnabled = this.voxtralCorrectionEnabled;
    if (previousMode === target) return;
    this.voxtralCorrectionMode = target;
    this.voxtralCorrectionEnabled = target !== 'off';
    this._postCorrectionMode(target, previousMode, previousEnabled);
  }

  private _postCorrectionMode(
    target: 'off' | 'fuzzy' | 'phonetic',
    previousMode: 'off' | 'fuzzy' | 'phonetic',
    previousEnabled: boolean,
  ): void {
    const sessionRunning = !!this.broadcast?.voxtralMini4bRealtimeEc2IP
      && this.broadcast.voxtralMini4bRealtimeEc2IP !== 'blocked';

    this.voxtralCorrectionToggling = true;
    this.voxtralError = '';
    this.http.post<any>(environment.API_Base + '/broadcast/voxtral-set-correction-mode', {
      broadcastKey: this.broadcast.key,
      mode: target,
    }).pipe(
      finalize(() => this.voxtralCorrectionToggling = false)
    ).subscribe({
      next: (resp: any) => {
        this.broadcast.voxtralMini4bRealtimeCorrectionMode = resp?.mode || target;
        this.broadcast.voxtralMini4bRealtimeCorrectionEnabled = target !== 'off';
        if (sessionRunning && resp?.live === true) {
          this.voxtralCorrectionPending = true;
          this.voxtralCorrectionPendingTarget = target;
        } else {
          this.voxtralCorrectionPending = false;
          this.voxtralCorrectionPendingTarget = null;
          if (sessionRunning && resp?.live === false) {
            this.voxtralError = resp?.liveReason === 'warming'
              ? 'Voxtral-Session wird gerade gestartet — Änderung wird erst beim nächsten Start angewendet.'
              : 'Live-Aktualisierung fehlgeschlagen — wird beim nächsten Start angewendet.';
          }
        }
      },
      error: (httpError: any) => {
        this.voxtralCorrectionMode = previousMode;
        this.voxtralCorrectionEnabled = previousEnabled;
        this.voxtralCorrectionPending = false;
        this.voxtralCorrectionPendingTarget = null;
        console.error('Fehler beim Aktualisieren des Voxtral-Korrektur-Modus:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Korrektur-Modus konnte nicht aktualisiert werden.';
      }
    });
  }

  /**
   * Back-compat shim. Routes the binary-checkbox path through the enum
   * handler so existing call sites keep working.
   */
  onCorrectionToggle(enabled: boolean): void {
    this.onCorrectionModeChange(enabled ? 'fuzzy' : 'off');
  }


  /**
   * Append one segment to the per-session timeline. Each WebSocket delivery
   * (PARTIAL or FINAL) produces exactly one entry. Oldest-first order is
   * reversed here so the UI sees newest on top.
   */
  private appendSegmentToTimeline(scope: 'partial' | 'final', text: string, accepted: any[], rejected: any[], llmAccepted: any[] = [], speaker: VoxtralSpeaker | null = null): void {
    if (!text) return;
    this.voxtralSegmentSeq += 1;
    // 2026-05-19 — split the mixed rejected payload from the WS into Stage 1
    // (fuzzy) and Stage 2 (LLM) lanes so the timeline renderer can show them
    // separately. The voxtral server now also splits at /segments, but WS
    // payloads still carry a single mixed array.
    const rejAll = rejected || [];
    const rejFuzzy = rejAll.filter((r: any) => (r?.stage || 'fuzzy') !== 'llm');
    const rejLlm = rejAll.filter((r: any) => (r?.stage || 'fuzzy') === 'llm');
    // Frontend #1 — maintain a running partials buffer of the in-progress
    // segment. For a partial: append THIS text first, then snapshot (so the
    // entry's `partials` includes the current snapshot — matches Part C
    // semantics in voxtral_server). For a final: snapshot what was buffered
    // for that segment, then clear so the next segment starts fresh.
    if (scope === 'partial') {
      this.voxtralCurrentSegmentPartials.push(text);
      if (this.voxtralCurrentSegmentPartials.length > 40) {
        this.voxtralCurrentSegmentPartials.shift();
      }
    }
    const partialsSnapshot = [...this.voxtralCurrentSegmentPartials];
    if (scope === 'final') {
      this.voxtralCurrentSegmentPartials = [];
    }
    this.voxtralSegmentTimeline.unshift({
      seq: this.voxtralSegmentSeq,
      scope,
      ts: Date.now(),
      text,
      accepted: accepted || [],
      rejected: rejFuzzy,
      llm_accepted: llmAccepted || [],
      llm_rejected: rejLlm,
      partials: partialsSnapshot,
      speaker,
    });
    if (this.voxtralSegmentTimeline.length > 500) {
      this.voxtralSegmentTimeline.length = 500;
    }
  }

  /**
   * One-shot history seed on dialog open. If the voxtral session is already
   * running, pull the existing in-memory timeline from the backend proxy so
   * the operator sees the full history, not just segments from "now".
   */
  private seedVoxtralSegmentTimeline(): void {
    if (!this.broadcast?.key || !this.broadcast?.voxtralMini4bRealtimeEc2IP) {
      return;
    }
    const url = `${environment.API_Base}/broadcast/voxtral-segments?broadcastKey=${encodeURIComponent(this.broadcast.key)}&limit=200`;
    this.http.get<any>(url).subscribe({
      next: (resp: any) => {
        const list = Array.isArray(resp?.segments) ? resp.segments : [];
        // Backend returns newest-first already; map into our shape.
        const mapped = list.map((r: any, idx: number) => {
          // The voxtral /segments endpoint splits rejected into rejected (Stage 1)
          // + llm_rejected (Stage 2). Older payloads (pre-v29) carried a mixed
          // `rejected` array; fall back to partitioning here so the UI handles
          // both shapes during the rollout window.
          const rejFromApi = Array.isArray(r.rejected) ? r.rejected : [];
          const llmRejFromApi = Array.isArray(r.llm_rejected) ? r.llm_rejected : null;
          const rejected = llmRejFromApi !== null
            ? rejFromApi
            : rejFromApi.filter((x: any) => (x?.stage || 'fuzzy') !== 'llm');
          const llm_rejected = llmRejFromApi !== null
            ? llmRejFromApi
            : rejFromApi.filter((x: any) => (x?.stage || 'fuzzy') === 'llm');
          return {
            seq: typeof r.seq === 'number' ? r.seq : (list.length - idx),
            scope: r.scope || 'final',
            ts: (r.ts || 0) * 1000,
            audio_start: r.audio_start,
            audio_end: r.audio_end,
            text: r.text || '',
            accepted: Array.isArray(r.accepted) ? r.accepted : [],
            rejected,
            llm_accepted: Array.isArray(r.llm_accepted) ? r.llm_accepted : [],
            llm_rejected,
            speaker: speakerFromRecord(r),
          };
        });
        // Only seed if we have nothing yet — avoid races with live WebSocket
        // deliveries that may have arrived while the HTTP call was in flight.
        if (this.voxtralSegmentTimeline.length === 0) {
          this.voxtralSegmentTimeline = mapped;
          this.voxtralSegmentSeq = mapped.length ? mapped[0].seq : 0;
        }
      },
      error: () => { /* swallow — endpoint may be unreachable when session stopped */ }
    });
  }

  // §6 perf — memoized filtered-segments view.
  // The template binds this getter; without memoization the `.filter()` ran
  // (and allocated a fresh array) on every change-detection pass, including
  // the ones triggered merely by opening a <select> or toggling a checkbox.
  // The cache key folds in the scope filter, the timeline length AND the head
  // segment's seq so it stays correct even at the 500-entry ring cap (where
  // length is constant but the head segment changes).
  private _filteredSegmentsCache: typeof this.voxtralSegmentTimeline = [];
  private _filteredSegmentsKey: string | null = null;

  get voxtralFilteredSegments() {
    const head = this.voxtralSegmentTimeline[0];
    const key = `${this.voxtralSegmentScopeFilter}|${this.voxtralSegmentTimeline.length}|${head ? head.seq : 0}`;
    if (key !== this._filteredSegmentsKey) {
      this._filteredSegmentsKey = key;
      this._filteredSegmentsCache = this.voxtralSegmentScopeFilter === 'all'
        ? this.voxtralSegmentTimeline
        : this.voxtralSegmentTimeline.filter(s => s.scope === this.voxtralSegmentScopeFilter);
    }
    return this._filteredSegmentsCache;
  }


  /**
   * F4: add a word to the local correction-context blocklist and re-serialise
   * the JSON so it's persisted on next Start. Affects future sessions only —
   * the corrector loads the blocklist once at session init.
   */
  addToBlocklist(word: string): void {
    if (!word) return;
    let ctx: any = {};
    try {
      ctx = this.voxtralCorrectionContext ? JSON.parse(this.voxtralCorrectionContext) : {};
    } catch {
      ctx = {};
    }
    if (!Array.isArray(ctx.blocklist)) ctx.blocklist = [];
    if (!ctx.blocklist.includes(word)) {
      ctx.blocklist.push(word);
      this.voxtralCorrectionContext = JSON.stringify(ctx, null, 2);
      this.voxtralCorrectionContextExpanded = true;
    }
  }

  /**
   * Persist the Korrektur-Kontext textarea contents to the broadcast row via
   * POST /broadcast/voxtral-correction-context. Until this existed, edits made
   * in the textarea were only carried into the next `voxtral-start` call —
   * which meant the operator's edits silently disappeared if they didn't
   * immediately click Start.
   */
  saveVoxtralCorrectionContext(): void {
    this.persistCorrectionContext(this.voxtralCorrectionContext || '');
  }

  /**
   * Phase 3.2 — `applyToBroadcast` callback fired by the new
   * <app-voxtral-correction-context-editor>. The editor hands us the
   * v2-canonical JSON; we POST it to /broadcast/voxtral-correction-context
   * and mirror to the local state so the panel doesn't flap on next refresh.
   */
  onCorrectionContextApplied(json: string): void {
    this.voxtralCorrectionContext = json;
    this.persistCorrectionContext(json);
  }

  private persistCorrectionContext(json: string): void {
    if (this.voxtralCorrectionContextSaving) return;
    this.voxtralCorrectionContextSaving = true;
    this.voxtralCorrectionContextError = '';
    this.voxtralCorrectionContextSavedAt = null;
    this.http.post(environment.API_Base + '/broadcast/voxtral-correction-context', {
      broadcastKey: this.broadcast.key,
      correctionContext: json
    }).pipe(
      finalize(() => this.voxtralCorrectionContextSaving = false)
    ).subscribe({
      next: () => {
        this.voxtralCorrectionContextSavedAt = new Date();
        this.broadcast.voxtralMini4bRealtimeCorrectionContext = json;
      },
      error: (httpError: any) => {
        console.error('Fehler beim Speichern des Korrektur-Kontexts:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralCorrectionContextError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Speichern fehlgeschlagen.';
      }
    });
  }

  /**
   * Split a voxtral transcript log-event payload into plain text + suffix metadata.
   * Format: "<text>[ ||CORR||<json>][ ||REJ||<json>][ ||LLM||<json>][ ||MODE||<str>]"
   * Each suffix is optional and independent; the parser locates them by string
   * search so out-of-order tolerance is automatic. ||MODE|| carries the
   * current correction_mode echoed by the session runner so the frontend can
   * detect when a live mode-change has taken effect.
   */
  private parseVoxtralPayload(payload: string): { text: string; corrections: any[]; rejected: any[]; llm_corrections: any[]; mode: string | null; models: string | null; speaker: VoxtralSpeaker | null } {
    const s = (payload || '').trim();
    const markers: Array<{ key: 'corr' | 'rej' | 'llm' | 'mode' | 'models' | 'spk'; sep: string; idx: number }> = [
      { key: 'corr',   sep: ' ||CORR||',   idx: s.indexOf(' ||CORR||') },
      { key: 'rej',    sep: ' ||REJ||',    idx: s.indexOf(' ||REJ||') },
      { key: 'llm',    sep: ' ||LLM||',    idx: s.indexOf(' ||LLM||') },
      { key: 'mode',   sep: ' ||MODE||',   idx: s.indexOf(' ||MODE||') },
      // §3.1 — echo of active models CSV. Clears the "pending model toggle"
      // badge once the next FINAL fires with the new selection.
      { key: 'models', sep: ' ||MODELS||', idx: s.indexOf(' ||MODELS||') },
      // 2026-09-28 — live speaker diarization (see voxtral-speaker.ts).
      { key: 'spk',    sep: ' ||SPK||',    idx: s.indexOf(' ||SPK||') },
    ];
    const present = markers.filter(m => m.idx >= 0).sort((a, b) => a.idx - b.idx);

    const parseArr = (raw: string): any[] => {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    if (present.length === 0) {
      return { text: s, corrections: [], rejected: [], llm_corrections: [], mode: null, models: null, speaker: null };
    }

    const text = s.substring(0, present[0].idx).trim();
    let corrections: any[] = [];
    let rejected: any[] = [];
    let llm_corrections: any[] = [];
    let mode: string | null = null;
    let models: string | null = null;
    let speaker: VoxtralSpeaker | null = null;

    for (let i = 0; i < present.length; i++) {
      const m = present[i];
      const start = m.idx + m.sep.length;
      const end = i + 1 < present.length ? present[i + 1].idx : s.length;
      const raw = s.substring(start, end);
      switch (m.key) {
        case 'corr':   corrections = parseArr(raw); break;
        case 'rej':    rejected = parseArr(raw); break;
        case 'llm':    llm_corrections = parseArr(raw); break;
        case 'mode':   mode = raw.trim() || null; break;
        case 'models': models = raw.trim(); break;  // empty string is meaningful (= cleared)
        case 'spk':    speaker = parseSpeakerMarker(raw.trim()); break;
      }
    }
    return { text, corrections, rejected, llm_corrections, mode, models, speaker };
  }

  /**
   * Build sanitized HTML that wraps corrected words in styled markup.
   *
   * Stage 1 (fuzzy) and Stage 2 (LLM) corrections carry positions in their
   * own snapshot of the text. By the time we render, `text` is the post-Stage-2
   * final string. We render LLM corrections preferentially (most authoritative);
   * for any non-overlapping Stage-1 entry that still matches the final text by
   * the `corrected` substring, we highlight it as fuzzy. Overlaps fall back to
   * the LLM style so the operator sees the chain's final result.
   */
  private renderWithCorrections(text: string, corrections: any[], llmCorrections: any[] = []): SafeHtml {
    if (!text) return '';
    const escape = (s: string) => s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const fuzzy = (corrections || []).filter((c) => c && typeof c.position === 'number' && c.corrected);
    const llm = (llmCorrections || []).filter((c) => c && typeof c.position === 'number' && c.corrected);
    if (fuzzy.length === 0 && llm.length === 0) {
      return this.sanitizer.bypassSecurityTrustHtml(escape(text));
    }
    // Tag each entry with its stage, sort by position, drop entries that do not
    // line up with the final text (they were superseded by a later stage).
    const tagged: Array<{ stage: 'fuzzy' | 'llm'; position: number; original: string; corrected: string }> = [];
    for (const c of llm) tagged.push({ stage: 'llm', position: c.position, original: c.original || '', corrected: c.corrected });
    for (const c of fuzzy) tagged.push({ stage: 'fuzzy', position: c.position, original: c.original || '', corrected: c.corrected });
    tagged.sort((a, b) => a.position - b.position || (a.stage === 'llm' ? -1 : 1));
    let out = '';
    let cursor = 0;
    for (const c of tagged) {
      const len = c.corrected.length;
      if (c.position < cursor) continue;
      if (c.position > text.length) break;
      if (text.substring(c.position, c.position + len) !== c.corrected) continue;
      out += escape(text.substring(cursor, c.position));
      const cls = c.stage === 'llm' ? 'voxtral-llm-correction' : 'voxtral-correction';
      const origCls = c.stage === 'llm' ? 'voxtral-llm-correction-original' : 'voxtral-correction-original';
      const corrCls = c.stage === 'llm' ? 'voxtral-llm-correction-corrected' : 'voxtral-correction-corrected';
      out += `<span class="${cls}"><span class="${origCls}">${escape(c.original)}</span><span class="${corrCls}">${escape(c.corrected)}</span></span>`;
      cursor = c.position + len;
    }
    out += escape(text.substring(cursor));
    return this.sanitizer.bypassSecurityTrustHtml(out);
  }

  terminateVoxtral() {
    if (!confirm('EC2-Instance wirklich terminieren? Die Instance wird zerstört (nicht nur gestoppt) und der warme GPU-Zustand geht verloren.')) return;

    this.voxtralTerminating = true;
    this.voxtralError = '';
    this.http.post(environment.API_Base + '/broadcast/voxtral-terminate', {
      broadcastKey: this.broadcast.key
    }).pipe(
      finalize(() => this.voxtralTerminating = false)
    ).subscribe({
      next: () => {
        this.voxtralCurrentText = '';
        this.voxtralCurrentHtml = '';
        this.voxtralTranscriptLines = [];
        this.voxtralTranscriptHtml = [];
        this.voxtralTranscriptSpeakers = [];
        this.voxtralCurrentSpeaker = null;
        this.voxtralRecentCorrections = [];
        this.voxtralRecentRejections = [];
        this.voxtralSegmentTimeline = [];
        this.voxtralSegmentSeq = 0;
        this.voxtralCurrentSegmentPartials = [];
        this.voxtralSystemMessages = [];
      },
      error: (httpError: any) => {
        console.error('Fehler beim Terminieren von Voxtral:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Fehler beim Terminieren der Voxtral-Instance.';
      }
    });
  }

  resetVoxtral() {
    if (!confirm('Voxtral-Session wirklich zurücksetzen? Falls aktiv, wird die Transkription gestoppt.')) return;

    this.voxtralResetting = true;
    this.voxtralError = '';
    this.http.post(environment.API_Base + '/broadcast/voxtral-reset', {
      broadcastKey: this.broadcast.key
    }).pipe(
      finalize(() => this.voxtralResetting = false)
    ).subscribe({
      next: () => {
        this.voxtralCurrentText = '';
        this.voxtralCurrentHtml = '';
        this.voxtralTranscriptLines = [];
        this.voxtralTranscriptHtml = [];
        this.voxtralTranscriptSpeakers = [];
        this.voxtralCurrentSpeaker = null;
        this.voxtralRecentCorrections = [];
        this.voxtralRecentRejections = [];
        this.voxtralSegmentTimeline = [];
        this.voxtralSegmentSeq = 0;
        this.voxtralCurrentSegmentPartials = [];
        this.voxtralSystemMessages = [];
      },
      error: (httpError: any) => {
        console.error('Fehler beim Zurücksetzen von Voxtral:', httpError);
        const serverMsg = httpError?.error;
        this.voxtralError = typeof serverMsg === 'string' && serverMsg.length > 0
          ? serverMsg
          : 'Fehler beim Zurücksetzen der Voxtral-Session.';
      }
    });
  }

  formatTranscriptDuration(seconds: number): string {
    if (!seconds) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${this.pad2(hours)}:${this.pad2(minutes)}:${this.pad2(secs)}`;
    } else {
      return `${this.pad2(minutes)}:${this.pad2(secs)}`;
    }
  }

  formatAudioDuration(seconds: number): string {
    if (!seconds) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${this.pad2(hours)}:${this.pad2(minutes)}:${this.pad2(secs)}`;
    } else {
      return `${this.pad2(minutes)}:${this.pad2(secs)}`;
    }
  }

  formatPcmsStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'PendingPcmsDownload': 'PCMS Download ausstehend',
      'PcmsDownloaded': 'PCMS heruntergeladen',
      'SrtGenerated': 'SRT generiert',
      'StlGenerated': 'STL generiert',
      'SrtOffsetGenerated': 'SRT (Offset) generiert',
      'StlOffsetGenerated': 'STL (Offset) generiert',
      'FabUploaded': 'FAB hochgeladen',
      // 2026-08-04 — STL war fertig, aber die Sendung lief bereits. Kein
      // UTDB-Upload, damit FAB weiter die Live-UT sendet.
      'StlPublishSkippedOnAir': 'STL nicht hochgeladen (Sendung lief bereits)'
    };
    return statusMap[status] || status;
  }
}
